// ResortReservations.jsx
import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Spinner } from "react-bootstrap";

import AddReservation from "./AddReservation";
import BookingReport from "./BookingReport";
import ReservationFilters from "./ReservationFilters";
import ReservationStats from "./ReservationStats";
import ReservationTable from "./ReservationTable";
import { deleteContent, getContents } from "../../../FIREBASECONFIG";

import { getUserRole } from "../../../APIS";

const ITEMS_PER_PAGE = 15;

const ResortReservations = () => {
  const collectionName = "reservations";
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  const role = getUserRole();
  const isAdmin = ["admin", "superadmin"].includes(
    (role || "").toLowerCase().replace(/\s+/g, "")
  );

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    searchTerm: "",
    status: "All",
    startDate: "",
    endDate: "",
    hotelId: "",
  });
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editReservation, setEditReservation] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [hotelOptions, setHotelOptions] = useState([]);

  /** 🔹 Fetch Reservations */
  const fetchReservations = async () => {
    try {
      setLoading(true);
      const data = await getContents(collectionName);
      setReservations(data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  /** 🔹 Fetch Hotels */
  useEffect(() => {
    const fetchHotels = async () => {
      try {
        let Hotels = await getContents("hotels");
        if (role === "hoteladmin" && userDetails?.hotelId) {
          Hotels = Hotels.filter((h) => h.id === userDetails.hotelId);
        }

        setHotelOptions(
          Hotels.map((h) => ({
            label: h.name,
            value: h.id,
            rawData: h,
          }))
        );
      } catch (err) {
        console.error("Hotel fetch error:", err);
      }
    };
    fetchHotels();
  }, []);

  /** 🔹 Filtered Reservations */

  const filteredReservations = useMemo(() => {
    const { status, startDate, endDate, hotelId } = filters;
    console.log({ status, startDate, endDate, hotelId });

    const filterData = reservations.filter((r) => {
      const checkIn = new Date(r.checkIn);
      // console.log("before:", reservations);
      // 🔹 Date Filter

      let matchDate = false;

      switch (true) {
        // 👉 Case 1: Date range filter applied
        case !!startDate && !!endDate:
          matchDate =
            moment(checkIn, "YYYY-MM-DD").isSameOrAfter(moment(startDate)) &&
            moment(checkIn, "YYYY-MM-DD").isSameOrBefore(moment(endDate));
          break;

        // 👉 Case 2: Only start date (single date search)
        case !!startDate:
          matchDate = moment(checkIn, "YYYY-MM-DD").isSameOrAfter(
            moment(startDate)
          );
          break;

        // 👉 Default: Only today
        default:
          matchDate = moment(checkIn).isSame(moment(), "month");
      }

      // // 🔹 Search Filter
      // const matchSearch =
      //   (r.bookingId || "").toLowerCase().includes(search) ||
      //   (r.firstName || "").toLowerCase().includes(search) ||
      //   (r.booker_email || "").toLowerCase().includes(search) ||
      //   (r.status || "").toLowerCase().includes(search);

      // 🔹 Status Filter
      const matchStatus =
        status === "All" ||
        (r.status || "").toLowerCase() === status.toLowerCase();

      // 🔹 Hotel Filter
      const matchHotel =
        !hotelId ||
        r.hotelId === hotelId ||
        (!isAdmin && r.hotelId === userDetails?.hotelId);
      //console.log("h=>", matchHotel);

      console.log(matchStatus);
      return matchStatus && matchDate && matchHotel;
    });
    // console.log("after:", filterData);
    return filterData;
  }, [
    reservations,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.hotelId,
    isAdmin,
    userDetails?.hotelId,
  ]);

  /** 🔹 Pagination */
  const totalPages = Math.ceil(filteredReservations.length / ITEMS_PER_PAGE);
  const paginatedReservations = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return filteredReservations.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredReservations, page]);

  /** 🔹 Actions */
  const handleDelete = async (id) => {
    try {
      await deleteContent(collectionName, id);
      setReservations((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("Error deleting reservation:", error);
    }
  };

  const handleEdit = (item) => {
    setEditReservation(item);
    setShowForm(true);
  };

  const toggleForm = async () => {
    if (showForm) await fetchReservations();
    setShowForm((prev) => !prev);
    setEditReservation(null);
  };

  return (
    <div className="col-12">
      <div className="card">
        {/* Header */}
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="mb-0">Hotel Reservations</h4>
          <ReservationFilters
            filters={filters}
            setFilters={setFilters}
            toggleForm={toggleForm}
            showForm={showForm}
            hotelOptions={hotelOptions}
          />
        </div>
        {/* <ReservationStats reservations={reservations} /> */}
        <ReservationStats reservations={filteredReservations} />

        {/* Body */}
        <div className="card-body">
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <Spinner animation="border" />
            </div>
          ) : showForm ? (
            <AddReservation
              role={role}
              userDetails={userDetails}
              reservation={editReservation}
              toggleForm={toggleForm}
            />
          ) : (
            <ReservationTable
              reservations={paginatedReservations}
              total={filteredReservations.length}
              page={page}
              totalPages={totalPages}
              setPage={setPage}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              setSelectedReservation={setSelectedReservation}
            />
          )}
        </div>
      </div>

      {/* Report Modal */}
      {selectedReservation && (
        <BookingReport
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
        />
      )}
    </div>
  );
};

export default ResortReservations;
